import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { AppNotification } from '../../models/notification.dto';
import { getRelativeTime } from '../../utils/relative-time.util';
import { getStatusPillStyle, StatusPillStyle } from '../../utils/status-pill.util';
import { InitialsAvatarComponent } from '../initials-avatar/initials-avatar.component';
import { HubspotMarkComponent } from '../hubspot-mark/hubspot-mark.component';

/**
 * Actor-led activity/notification item — actor avatar + inline "{Actor} {verb} {subject}"
 * sentence, optional comment preview, optional "Status: [from] → [to]" pills (stage changes),
 * and relative time. Shared by the org-dashboard Activity card and the notifications panel.
 */
@Component({
  selector: 'app-activity-item',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, InitialsAvatarComponent, HubspotMarkComponent],
  templateUrl: './activity-item.component.html',
  styleUrls: ['./activity-item.component.scss']
})
export class ActivityItemComponent {
  @Input() notification!: AppNotification;

  private get d(): Record<string, any> {
    return (this.notification?.data as Record<string, any>) ?? {};
  }

  /**
   * Webflow's own events (a branch deleted there) have no Declunk actor: Webflow is named instead.
   * A released hold names the request whose completion or deletion released it.
   */
  get actorName(): string {
    if (this.notification.type === 'request.branch_deleted') return 'Webflow';
    if (this.notification.type === 'request.hold_released') return this.d['released_by_ticket_name'] || 'A request';
    if (this.notification.type === 'request.hubspot_message') return this.d['sender_name'] || 'The client';
    return this.d['actor_name'] || 'Someone';
  }

  /**
   * An actor with no Declunk account, drawn with HubSpot's mark instead of initials: the Client
   * emailing through HubSpot is not a person the app knows.
   */
  get isHubspotActor(): boolean {
    return this.notification.type === 'request.hubspot_message';
  }
  get ticketTitle(): string { return this.d['ticket_title'] || this.d['ticket_name'] || ''; }
  get projectName(): string { return this.d['project_name'] || ''; }
  get siteName(): string { return this.d['site_name'] || ''; }
  get commentPreview(): string { return this.d['comment_preview'] || this.d['latest_reply_preview'] || ''; }
  get relativeTime(): string { return getRelativeTime(this.notification.created_at); }

  get fromStage(): string | undefined { return this.d['from_stage']; }
  get toStage(): string | undefined { return this.d['to_stage']; }
  private get fromColor(): string | undefined { return this.d['from_stage_color']; }
  private get toColor(): string | undefined { return this.d['to_stage_color']; }

  get showComment(): boolean {
    return ['comment.created', 'comment.mention', 'comment.reply'].includes(this.notification.type)
      && !!this.commentPreview;
  }

  get showStatus(): boolean {
    return this.notification.type === 'request.updated' && !!this.fromStage && !!this.toStage;
  }

  /** Verb phrase between the actor name and the (bold) subject. */
  get verb(): string {
    switch (this.notification.type) {
      case 'comment.mention': return 'mentioned you in';
      case 'comment.created': return 'commented on';
      case 'comment.reply': return 'replied on';
      case 'task.assigned': return 'assigned you to';
      case 'request.created': return 'created';
      case 'request.deleted': return 'deleted a request in';
      case 'request.review_requested': return 'requested your review on';
      case 'request.hubspot_message': return 'emailed about';
      case 'request.branch_deleted': return 'reports the branch was deleted for';
      case 'request.hold_released':
        return this.d['reason'] === 'deleted'
          ? 'was deleted, releasing the hold on'
          : 'was Completed, releasing the hold on';
      case 'request.updated': {
        const to = (this.toStage || '').toLowerCase();
        if (/(denied|rejected)/.test(to)) return 'denied the request';
        if (/(complete|approved|done)/.test(to)) return 'approved the request';
        return 'moved';
      }
      case 'site.published': return 'published changes to';
      default: return 'updated';
    }
  }

  /** The bold subject after the verb — the ticket title (or project for delete events). */
  get subject(): string {
    switch (this.notification.type) {
      case 'request.deleted': return this.projectName;
      case 'site.published': return `${this.siteName} in ${this.projectName}`;
      default: return this.ticketTitle;
    }
  }

  fromPill(): StatusPillStyle { return getStatusPillStyle(this.fromStage, this.fromColor); }
  toPill(): StatusPillStyle { return getStatusPillStyle(this.toStage, this.toColor); }
}
